// src/utils/notifications.js
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

// EmailJS config from .env
const SERVICE_ID     = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const PUBLIC_KEY     = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const ORDER_TEMPLATE = process.env.REACT_APP_EMAILJS_ORDER_TEMPLATE;
const SHIP_TEMPLATE  = process.env.REACT_APP_EMAILJS_SHIPPED_TEMPLATE;

// Load EmailJS SDK lazily
async function loadEmailJS() {
  if (window.emailjs) return window.emailjs;
  return new Promise((resolve, reject) => {
    const script   = document.createElement('script');
    script.src     = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';
    script.onload  = () => { window.emailjs.init(PUBLIC_KEY); resolve(window.emailjs); };
    script.onerror = () => reject(new Error('EmailJS failed to load'));
    document.head.appendChild(script);
  });
}

// ── Order Confirmation Email ─────────────────────────────────────
export async function sendOrderConfirmation(order) {
  if (!SERVICE_ID || !ORDER_TEMPLATE || !PUBLIC_KEY) {
    console.log('EmailJS not configured — skipping order confirmation email');
    return;
  }
  try {
    const ejs       = await loadEmailJS();
    const addr      = order.shippingAddress || {};
    const itemsList = (order.items || [])
      .map((i) => `${i.name} × ${i.quantity} — ₹${(i.price * i.quantity).toLocaleString('en-IN')}`)
      .join('\n');

    await ejs.send(SERVICE_ID, ORDER_TEMPLATE, {
      to_email:         order.userEmail,
      to_name:          addr.name || 'Customer',
      order_id:         (order.id || '').slice(-8).toUpperCase(),
      items_list:       itemsList,
      total:            `₹${(order.total || 0).toLocaleString('en-IN')}`,
      delivery_address: `${addr.address}, ${addr.city}, ${addr.state} — ${addr.pincode}`,
      payment_status:   order.paymentStatus === 'paid' ? 'Paid Online ✓' : 'Cash on Delivery',
      track_url:        `${window.location.origin}/track/${order.id}`,
    });
    console.log('✅ Order confirmation email sent');
  } catch (err) {
    console.error('Order email failed (non-critical):', err);
  }
}

// ── Shipped Notification Email ───────────────────────────────────
export async function sendShippedNotification(order) {
  if (!SERVICE_ID || !SHIP_TEMPLATE || !PUBLIC_KEY) {
    console.log('EmailJS not configured — skipping shipped email');
    return;
  }
  try {
    const ejs  = await loadEmailJS();
    const addr = order.shippingAddress || {};
    await ejs.send(SERVICE_ID, SHIP_TEMPLATE, {
      to_email:  order.userEmail,
      to_name:   addr.name || 'Customer',
      order_id:  (order.id || '').slice(-8).toUpperCase(),
      awb_code:  order.awbCode || '—',
      courier:   order.courierName || 'Our delivery partner',
      track_url: `${window.location.origin}/track/${order.id}`,
    });
    console.log('✅ Shipped notification email sent');
  } catch (err) {
    console.error('Shipped email failed (non-critical):', err);
  }
}

// ── Browser Notification for Merchant ───────────────────────────
export function showMerchantOrderAlert(orderCount = 1) {
  if (!('Notification' in window)) return;
  Notification.requestPermission().then((perm) => {
    if (perm === 'granted') {
      new Notification('🛍️ New Order on CoupleCart!', {
        body:  `You have ${orderCount} new order${orderCount > 1 ? 's' : ''} waiting.`,
        icon:  '/logo192.png',
        badge: '/logo192.png',
        tag:   'new-order',
      });
    }
  });
}

// ── Real-time Merchant Order Listener ───────────────────────────
let _unsubscribe = null;
let _lastCount   = null;

export function startMerchantOrderListener(merchantId, onNewOrder) {
  // Clean up any existing listener
  if (_unsubscribe) { _unsubscribe(); _unsubscribe = null; }

  const q = query(
    collection(db, 'merchantOrders'),
    where('merchantId', '==', merchantId),
    where('status', '==', 'confirmed')
  );

  _unsubscribe = onSnapshot(q, (snap) => {
    const count = snap.size;
    if (_lastCount !== null && count > _lastCount) {
      const diff = count - _lastCount;
      showMerchantOrderAlert(diff);
      if (onNewOrder) onNewOrder(diff);
    }
    _lastCount = count;
  }, (err) => {
    console.error('Order listener error:', err);
  });

  return _unsubscribe;
}

export function stopMerchantOrderListener() {
  if (_unsubscribe) {
    _unsubscribe();
    _unsubscribe = null;
    _lastCount   = null;
  }
}
